// Bottom-sheet geometry, kept pure so it can be reasoned about (and tested)
// without a browser. The component owns the pointer plumbing; this owns the
// arithmetic, which is the part that actually goes wrong.

/** Strip of map the sheet always leaves uncovered at the top. Small, because
 *  the search card is hidden while the sheet is open — there is nothing up
 *  there to clear any more, only the need to keep some map in view. */
export const SHEET_TOP_RESERVE = 100;
/** A long list stops here; the rest of the screen still shows the map. */
export const SHEET_MAX_FRACTION = 0.65;
/** Fallback floor for before the sheet has been measured. The real floor is
 *  furniture + one row, measured from the DOM — see sheetLimits. */
export const SHEET_MIN_HEIGHT = 190;
/** Drag this far past the bottom stop and releasing dismisses the sheet. */
export const SHEET_DISMISS_SLOP = 70;

/**
 * How short and how tall the sheet may be.
 *
 * The ceiling is the *smaller* of half the screen and what the content actually
 * needs — so a two-row list stops at two rows instead of leaving a slab of
 * blank space, and dragging locks there because there is nothing more to show.
 * A long list stops at half the screen so the map stays readable.
 *
 * `contentHeight` is the sheet's natural height (furniture + full list); pass
 * null before it has been measured and only the screen cap applies.
 *
 * `oneRowHeight` is the sheet's height with exactly one row showing (furniture
 * + the first row). It becomes the floor, so shrinking or dragging the sheet
 * down can never hide the list entirely — there is always a study spot, or a
 * washroom, left to look at. A guessed constant got this wrong in both
 * directions, since the furniture changes height with the filter row.
 */
export function sheetLimits(hostHeight, contentHeight = null, oneRowHeight = null) {
  const host = Number.isFinite(hostHeight) && hostHeight > 0 ? hostHeight : 800;
  const roomAbove = Math.max(240, host - SHEET_TOP_RESERVE);
  const ceiling = Math.min(roomAbove, Math.round(host * SHEET_MAX_FRACTION));
  const max = Number.isFinite(contentHeight) && contentHeight > 0
    ? Math.max(120, Math.min(ceiling, Math.ceil(contentHeight)))
    : ceiling;
  const floor = Number.isFinite(oneRowHeight) && oneRowHeight > 0
    ? Math.ceil(oneRowHeight)
    : SHEET_MIN_HEIGHT;
  // The floor gives way to the ceiling: a sheet whose whole content is shorter
  // than one row's worth of furniture should not be padded out past it.
  return { min: Math.min(floor, max), max, host, ceiling };
}

/**
 * Where the sheet opens: as tall as it is allowed to be, unless a caller asks
 * for a particular fraction of the screen.
 *
 * A list wants all the room it can get — the whole question is "what else is
 * there" — so the default is the ceiling. A single thing being read is the
 * other case: a bus stop's timetable is ten departures long and would open
 * covering most of the map, when the useful part is the first two lines and the
 * map showing where the stop is. `fraction` is that opinion, still clamped by
 * both limits, so a stop with nothing scheduled stops at its own content
 * height rather than opening onto a slab of blank sheet.
 */
export function defaultSheetHeight(
  hostHeight, contentHeight = null, oneRowHeight = null, fraction = null, minFraction = null,
) {
  const { min, max, host, ceiling } = sheetLimits(hostHeight, contentHeight, oneRowHeight);
  const want = Number.isFinite(fraction) && fraction > 0 ? Math.round(host * fraction) : max;
  // A guaranteed share of the screen, which the measured content may NOT pull
  // the sheet below.
  //
  // Everywhere else "no taller than the content" is the right rule — a sheet
  // with a slab of blank space under its last row looks broken. A bus stop is
  // the exception, and the reason is timing: its departures arrive from the
  // network *after* the sheet opens, so at the moment the height is chosen the
  // content is one "Loading…" line. Sized to that, the sheet opens as a strip
  // and the timetable then loads inside it. The floor is what stops a slow
  // answer deciding how big the answer's box is.
  const floor = Number.isFinite(minFraction) && minFraction > 0
    ? Math.min(ceiling, Math.round(host * minFraction))
    : 0;
  return Math.round(Math.max(min, floor, Math.min(Math.max(max, floor), want)));
}

/**
 * Height while dragging. `raw` is the unclamped value — the caller keeps it so
 * that a drag which runs well past the bottom stop can dismiss on release,
 * which clamping alone would hide.
 *
 * Dragging **up** (a smaller clientY) makes the sheet taller.
 */
export function dragHeight({ startHeight, startY, currentY, min, max }) {
  const raw = startHeight + (startY - currentY);
  return { raw, height: Math.round(Math.max(min, Math.min(max, raw))) };
}

/** Was the release far enough below the bottom stop to mean "put it away"? */
export function shouldDismiss(raw, min) {
  return raw < min - SHEET_DISMISS_SLOP;
}
